import React, {useCallback, useEffect, useState} from "react";
import {DialogEx} from "@/lib/components/DialogEx";
import {CartonGradeCatalogDTO} from "@/api/data-contracts";
import {OrderDataType} from "@/model/orderData";
import {useTranslation} from "react-i18next";
import {RequestI18N} from "@/translations";
import {CartonGradeForm} from "./CartonGradeForm";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {CartonGradeSelectorTable} from "@/common/dialogs/CartonGradeDialog/CartonGradeSelectorTable";

export const CartonGradeSelectorDialog: React.FC<{
    open: boolean;
    close: () => void;
    onSelect: (grade: CartonGradeCatalogDTO) => void;
    grades: CartonGradeCatalogDTO[];
    initialFilter?: string;
    mode: OrderDataType;
    selectedLocations?: string[];
    onLocationsChange?: (locations: string[]) => void;
    originalGrade?: CartonGradeCatalogDTO;
    selectedGrade?: CartonGradeCatalogDTO;
}> = ({
    open,
    close,
    onSelect,
    grades,
    initialFilter = "",
    mode,
    selectedLocations = [],
    onLocationsChange,
    originalGrade,
    selectedGrade
}) => {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [internalSelectedLocations, setInternalSelectedLocations] = useState<string[]>(selectedLocations);
    const [showCustomGradeForm, setShowCustomGradeForm] = useState(false);
    const [customFormRef, setCustomFormRef] = useState<{ submit: () => void } | null>(null);

    useEffect(() => {
        if (open) {
            setInternalSelectedLocations(selectedLocations);
            if (selectedGrade && selectedGrade.importedAt === undefined) {
                setShowCustomGradeForm(true);
            } else {
                setShowCustomGradeForm(false);
            }
            setCustomFormRef(null);
        }
    }, [selectedLocations, open, selectedGrade]);

    useEffect(() => {
        if (onLocationsChange && mode === "quote") {
            onLocationsChange(internalSelectedLocations);
        }
    }, [mode, internalSelectedLocations, onLocationsChange]);

    // Memoize callbacks to prevent unnecessary re-renders
    const handleCustomGradeSubmit = useCallback(
        (grade: CartonGradeCatalogDTO) => {
            onSelect(grade);
            close();
        },
        [onSelect, close]
    );

    const handleSetFormRef = useCallback((ref: { submit: () => void } | null) => {
        setCustomFormRef(ref);
    }, []);

    const toggleCustomGradeForm = useCallback(() => {
        setShowCustomGradeForm((prev) => !prev);
        setCustomFormRef(null); // Reset form ref when toggling
    }, []);

    const handleApplySelection = useCallback(() => {
        const sel = grades.find((g) => g.id === selectedId);
        if (sel) {
            onSelect(sel);
            close();
        }
    }, [grades, selectedId, onSelect, close]);

    const handleCustomFormApply = useCallback(() => {
        if (customFormRef) {
            customFormRef.submit();
        }
    }, [customFormRef]);

    return (
        <DialogEx
            open={open}
            onClose={close}
            maxWidth={showCustomGradeForm ? "sm" : "xl"}
            fullWidth
            title={showCustomGradeForm ? t(RequestI18N.grade.dialog.editTitle) : t(RequestI18N.grade.dialog.selectTitle)}
            allowFullScreen
            contentSx={showCustomGradeForm ? undefined : { overflow: "hidden" }}
            buttons={
                <>
                    {!showCustomGradeForm && (
                        <ButtonEx
                                label={t(RequestI18N.grade.dialog.customGradeButton)}
                            onClick={toggleCustomGradeForm}
                        />
                    )}
                    {!showCustomGradeForm && (
                        <ButtonEx
                                label={t(RequestI18N.grade.dialog.applyButton)}
                            disabled={!selectedId}
                            onClick={handleApplySelection}
                        />
                    )}
                    {showCustomGradeForm && (
                        <>
                            <ButtonEx
                                    label={t(RequestI18N.grade.dialog.progroupGradeButton)}
                                onClick={() => setShowCustomGradeForm(false)}
                            />

                            <ButtonEx label={t(RequestI18N.grade.dialog.applyButton)} onClick={handleCustomFormApply}/>
                        </>
                    )}
                </>
            }
        >
            {showCustomGradeForm ? (
                <CartonGradeForm
                    originalGrade={originalGrade}
                    selectedGrade={selectedGrade}
                    showOriginalGrade={mode === "quote" && !!originalGrade}
                    onSubmit={handleCustomGradeSubmit}
                    setFormRef={handleSetFormRef}
                />
            ) : (
                <CartonGradeSelectorTable
                    grades={grades}
                    initialFilter={initialFilter}
                    mode={mode}
                    selectedLocations={selectedLocations}
                    onLocationsChange={onLocationsChange}
                    onSelect={onSelect}
                    originalGrade={originalGrade}
                    selectedGrade={selectedGrade}
                    setSelectedId={setSelectedId}
                    selectedId={selectedId}
                />
            )}
        </DialogEx>
    );
};
